import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDemoSafeMutation } from "@/hooks/useDemoGuard";
import { contractsRepository } from "@/repositories/contracts";
import { nafathVerificationsRepository } from "@/repositories/nafathVerifications";
import { ownersRepository } from "@/repositories/owners";
import { propertiesRepository } from "@/repositories/properties";
import { receiptsRepository } from "@/repositories/receipts";
import { unitsRepository } from "@/repositories/units";
import { expensesRepository } from "@/repositories/expenses";
import { useAuth } from "./useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import {
  assertPhoneAvailableAcrossCompanies,
  assertNationalIdAvailableAcrossCompanies,
  assertEmailAvailableAcrossCompanies,
} from "@/lib/crossCompanyCheck";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { identityReserveUser, API_URL } from "@/services/api";
import { CustomRole } from "@/lib/rbac";
import { normalizePhoneForDB, toGoTruePhone } from "@/lib/phone";

export interface OwnerWithStats {
  id: string;
  full_name: string;
  full_name_en: string | null;
  email: string | null;
  phone: string | null;
  national_id: string | null;
  address: string | null;
  is_active: boolean | null;
  invitation_status: string;
  /** Linked auth user — non-null means the owner has logged in at least once. */
  user_id: string | null;
  created_at: string;
  date_of_birth?: string | null;
  /** Nafath verification data, attached by the list query for verified owners. */
  nafath?: Record<string, unknown> | null;
  // Aggregated stats
  properties_count: number;
  units_count: number;
  total_income: number;
  total_fees: number;
}

/**
 * Owner entity metadata (type + establishment + representative names) keyed by
 * owner id. A deliberately narrow projection so pickers/labels can distinguish
 * individual vs organization owners WITHOUT widening the heavier owners list
 * (which goes through the Nest stats query). One batched read per company.
 *
 * The actual data access lives in `ownersRepository.getEntityMeta` — the repo
 * is the only place allowed to touch `owners` data.
 */
export function useOwnerEntityMeta() {
  const { companyId, user } = useAuth();
  const { hasPermission } = usePermissions();

  // SAME gate as useOwners — this is NOT optional. getEntityMeta now goes through
  // the `get_owner_entity_meta` SECURITY DEFINER RPC, which raises on insufficient
  // permission rather than silently returning nothing. AddPropertyDialog is mounted
  // by the global QuickAddMenu in the shared shell, so this hook fires on EVERY
  // authenticated page — including the employee portal, where the active role is
  // `employee`. Before the RPC fix, firing it unguarded sent `x-active-role:
  // employee` at GET /rest/v1/owners and the correlated-subquery RLS path timed
  // out, surfacing as a 500 (Sentry DALLALI-FRONTEND-DA, /portal/employee/properties).
  // Gating on owners.view + a live session matches useOwners (Sentry #1843/#1891):
  // no owners.view ⇒ the owner list is empty anyway ⇒ there's no metadata to fetch,
  // and it avoids hitting the RPC's raise-on-deny path unnecessarily.
  const canViewOwners = hasPermission("owners.view");

  return useQuery({
    // enabled gates on companyId, so the non-null assertion is safe.
    queryKey: ["owner-entity-meta", companyId],
    queryFn: () => ownersRepository.getEntityMeta(companyId!),
    enabled: !!user?.id && !!companyId && canViewOwners,
    staleTime: 5 * 60 * 1000,
  });
}

export interface CreateOwnerData {
  full_name: string;
  full_name_en?: string | null;
  national_id?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  // Commercial-entity + representative fields (migration 20260521120000).
  // Optional so existing individual-owner callers are unaffected.
  owner_type?: "individual" | "commercial";
  commercial_name?: string | null;
  commercial_name_en?: string | null;
  unified_number?: string | null;
  cr_number?: string | null;
  vat_number?: string | null;
  representative_name?: string | null;
  representative_name_en?: string | null;
  representative_id_number?: string | null;
  representative_phone?: string | null;
  date_of_birth?: string | null;
}

/**
 * Create-only input: CreateOwnerData plus the identity-containment override.
 * Deliberately NOT part of CreateOwnerData — useUpdateOwner spreads
 * Partial<CreateOwnerData> straight into the owners-table update, and
 * reserve_full_name must never reach an owners row.
 */
export type CreateOwnerInput = CreateOwnerData & {
  /**
   * Identity containment (#3351): the name forwarded to identityReserveUser.
   * - key absent → full_name is forwarded (default; existing callers unchanged)
   * - null → NO name is sent to identity (the caller's full_name is a
   *   display-only fallback, e.g. CreateContractDialog's `مالك - <id>`,
   *   which must never become the person's profile name)
   * - string → that value is forwarded
   * Never affects the owners row itself — full_name is always used there.
   */
  reserve_full_name?: string | null;
};

// Hook to get all owners with their aggregated stats
export function useOwners() {
  const { companyId, user } = useAuth();
  const { hasPermission } = usePermissions();

  // Permission gate only — owners are NOT scoped by employee property
  // assignments. An employee with owners.view sees every owner in the
  // company. Property-assignment scoping still applies to property/unit/
  // contract-bound resources, but not to the people directories.
  const canViewOwners = hasPermission("owners.view");

  return useQuery({
    queryKey: ["owners-with-stats", companyId],
    queryFn: async () => {
      if (!companyId) return [];

      // Owners + per-owner stats in ONE server-side aggregate
      // (get_owners_with_stats). Replaces the old browser-side fan-out — a
      // ~90-element `owner_id IN (...)` on `properties` with an embedded `units`
      // join — that timed out under RLS on large companies and hung the Owners
      // tab on prod (2026-06-08 incident). See ownersRepository.listWithStats.
      const owners = await ownersRepository.listWithStats(companyId);

      if (owners.length === 0) {
        return [];
      }

      // ── Batch-fetch nafath_verifications for verified owners ──────────────
      // So getOwnerDob / getOwnerName selectors can resolve from the list too.
      const nafathMap = new Map<string, Record<string, unknown>>();
      const verifiedOwners = owners.filter((o) => o.status === "verified");
      if (verifiedOwners.length > 0) {
        const verifiedNationalIds = verifiedOwners
          .map((o) => o.national_id)
          .filter((v): v is string => Boolean(v));
        if (verifiedNationalIds.length > 0) {
          const nafathRows =
            await nafathVerificationsRepository.listCompletedByNationalIds(
              verifiedNationalIds,
              companyId,
            );
          if (nafathRows) {
            for (const row of nafathRows) {
              nafathMap.set(row.national_id, row);
            }
          }
        }
      }

      // Build final result. Counts/amounts come back from the aggregate
      // (bigint/numeric → coerce to number); the shape is unchanged.
      const ownersWithStats: OwnerWithStats[] = owners.map((owner) => ({
        id: owner.id,
        full_name: owner.full_name || "",
        full_name_en: owner.full_name_en,
        email: owner.email,
        phone: owner.phone,
        national_id: owner.national_id,
        address: owner.address,
        is_active: owner.is_active,
        invitation_status: owner.status || "not_invited",
        user_id: owner.user_id ?? null,
        created_at: owner.created_at,
        date_of_birth: owner.date_of_birth ?? null,
        nafath: owner.national_id
          ? nafathMap.get(owner.national_id) ?? null
          : null,
        properties_count: Number(owner.properties_count),
        units_count: Number(owner.units_count),
        total_income: Number(owner.total_income),
        total_fees: Number(owner.total_fees),
      }));

      return ownersWithStats;
    },
    // Gate on an authenticated user, not just a cached companyId. The
    // `owners_with_status` view grants SELECT to authenticated/service_role only
    // (owner data isn't public), so firing this while the session is `anon`
    // (e.g. a token-expiry / re-auth window on /auth, where companyId is still
    // cached) throws a hard "permission denied for view owners_with_status"
    // (Sentry DALLALI-FRONTEND-AR, issue #1843). Matches the sibling auth-gated
    // hooks (useTenantData / usePermissions: `user?.id && companyId`).
    //
    // Also gate on canViewOwners permission — checking inside queryFn still allows
    // the query to execute (and hit RLS/view permission errors) before the early
    // return. Moving the check to enabled prevents the query from running at all
    // (Sentry DALLALI-FRONTEND-AV, issue #1891).
    enabled: !!user?.id && !!companyId && canViewOwners,
  });
}

// Hook to get owner stats counts
export function useOwnersStats() {
  const { data: owners = [], isLoading } = useOwners();

  const stats = {
    total: owners.length,
    active: owners.filter((o) => o.is_active !== false).length,
    inactive: owners.filter((o) => o.is_active === false).length,
    totalProperties: owners.reduce((sum, o) => sum + o.properties_count, 0),
    totalUnits: owners.reduce((sum, o) => sum + o.units_count, 0),
    totalIncome: owners.reduce((sum, o) => sum + o.total_income, 0),
    totalFees: owners.reduce((sum, o) => sum + o.total_fees, 0),
  };

  return { data: stats, isLoading };
}

// Hook to create an owner
export function useCreateOwner() {
  const { companyId } = useAuth();
  const queryClient = useQueryClient();
  const { language } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (data: CreateOwnerInput) => {
      if (!companyId) throw new Error("Company not found");

      await assertPhoneAvailableAcrossCompanies(data.phone, companyId, language as "ar" | "en");
      await assertNationalIdAvailableAcrossCompanies(data.national_id, companyId, language as "ar" | "en");
      await assertEmailAvailableAcrossCompanies(data.email, companyId, language as "ar" | "en");

      const owner = await ownersRepository.create({
        company_id: companyId,
        full_name: data.full_name,
        full_name_en: data.full_name_en || null,
        national_id: data.national_id || null,
        phone: data.phone || null,
        email: data.email || null,
        address: data.address || null,
        is_active: true,
        invitation_status: "active",
        owner_type: data.owner_type || "individual",
        commercial_name: data.commercial_name || null,
        commercial_name_en: data.commercial_name_en || null,
        unified_number: data.unified_number || null,
        cr_number: data.cr_number || null,
        vat_number: data.vat_number || null,
        representative_name: data.representative_name || null,
        representative_name_en: data.representative_name_en || null,
        representative_id_number: data.representative_id_number || null,
        representative_phone: data.representative_phone || null,
        date_of_birth: data.date_of_birth || null,
      });

      // Phase 1: reserve a user identity BEFORE invite
      // (non-blocking: if reserve fails or is skipped, the fallback at invite-accept still works).
      // Canonicalize to 966XXXXXXXXX first, then prepend '+' exactly once.
      // data.phone may arrive as E.164 (+966…), canonical (966…), or local (5…)
      // depending on the caller — raw `+${data.phone}` produced '++966…' for E.164
      // input (silent reserve-user 400). No valid Saudi mobile → skip the park;
      // reserve-user requires E.164 and the invite-accept fallback still covers it.
      const canonicalReservePhone = normalizePhoneForDB(data.phone);
      // #3351: never propagate a display-only placeholder name into identity.
      // `reserve_full_name` (tri-state, see CreateOwnerData) decides what the
      // reserve call carries; the owners row above always keeps full_name.
      const reserveFullName =
        data.reserve_full_name === undefined
          ? data.full_name
          : data.reserve_full_name;
      if (API_URL && owner?.id && canonicalReservePhone) {
        try {
          const { user_id } = await identityReserveUser({
            phone: toGoTruePhone(canonicalReservePhone),
            national_id: data.national_id || undefined,
            full_name: reserveFullName || undefined,
            // Park-at-create: grant the owner role + company up front so the
            // person can log in immediately and shows correctly in admin views.
            company_id: companyId,
            role: "owner",
            custom_role: CustomRole.PropertyOwner,
          });
          await ownersRepository.update(owner.id, { user_id }, companyId);
        } catch {
          // Non-blocking — the fuzzy fallback at invite-accept still covers this.
        }
      }

      return owner;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owners-with-stats"] });
      // Entity metadata (individual/organization, establishment + representative)
      // backs the owner pickers — refresh it so a just-created organization owner
      // renders with its establishment name immediately, not as an individual.
      queryClient.invalidateQueries({ queryKey: ["owner-entity-meta"] });
    },
  });
}

// Person-resolution delegated to ownersRepository.resolvePersonIds()
// which unions by national_id AND user_id, covering both staff-created
// and login-linked owner rows for the same person.

// Hook to get single owner details
export function useOwnerDetails(ownerId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["owner-details", ownerId, companyId],
    queryFn: async () => {
      if (!ownerId || !companyId) return null;

      // getWithStatus queries the owners_with_status view via the repository
      // (same source as the Edit-dialog lock), returning owner data +
      // nafath_verified boolean.
      return await ownersRepository.getWithStatus(ownerId, companyId);
    },
    enabled: !!ownerId && !!companyId,
  });
}

// Hook to fetch a single owner's full record BY ID (owners table row,
// select '*') — used to prefill fields the list query (OwnerWithStats) doesn't
// carry, e.g. vat_number. getById guarantees the value is present (not the
// status view), so an edit-form prefill is safe on every path.
//
// Distinct name + query key from useOwnerData's `useOwnerRecord()` (the
// owner-portal self-record by user_id) so the two never share the cache key.
export function useOwnerFullRecord(ownerId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["owner-full-record", ownerId, companyId],
    queryFn: () =>
      ownerId && companyId ? ownersRepository.getById(ownerId, companyId) : null,
    enabled: !!ownerId && !!companyId,
  });
}

// Hook to get owner's properties — resolves the full person portfolio
// (all owner-row fragments + unit-level ownership on multi-owner properties).
export function useOwnerPropertiesList(ownerId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["owner-properties-list", ownerId, companyId],
    queryFn: async () => {
      if (!ownerId || !companyId) return [];

      // Resolve the person's full set of owner-row IDs
      const ownerIds = await ownersRepository.resolvePersonIds(ownerId, companyId);

      // 1. Direct properties (properties.owner_id IN ownerIds)
      const properties = await propertiesRepository.listWithUnitsByOwnerIds(
        companyId,
        ownerIds,
      );

      // 2. Unit-level ownership on multi-owner properties
      // (pattern mirroring list-page aggregation in useOwners:142-161)
      const unitOwned = await unitsRepository.listOwnedByOwnerIds(
        companyId,
        ownerIds,
      );

      const result = [...(properties || [])];
      const directPropIds = new Set(properties?.map((p) => p.id) ?? []);

      // Group unit-owned entries by their parent property, excluding
      // properties already fetched via direct owner_id.
      const byProperty = new Map<string, typeof unitOwned>();
      unitOwned?.forEach((u) => {
        if (u.property_id && !directPropIds.has(u.property_id)) {
          if (!byProperty.has(u.property_id)) {
            byProperty.set(u.property_id, []);
          }
          byProperty.get(u.property_id)!.push(u);
        }
      });

      // 3. Fetch the multi-owner properties themselves (their owner_id IS NULL
      //    but they hold units linked to the person).
      if (byProperty.size > 0) {
        const missingPropIds = Array.from(byProperty.keys());
        const multiProps = await propertiesRepository.listByIds(
          companyId,
          missingPropIds,
        );

        multiProps?.forEach((p) => {
          const units = byProperty.get(p.id) || [];
          result.push({ ...p, units } as typeof result[number]);
        });
      }

      return result;
    },
    enabled: !!ownerId && !!companyId,
  });
}

// Hook to get owner's financial summary — resolves the full person portfolio
export function useOwnerFinancialSummary(ownerId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["owner-financial-summary", ownerId, companyId],
    queryFn: async () => {
      if (!ownerId || !companyId) return null;

      // Resolve person's owner-row IDs
      const ownerIds = await ownersRepository.resolvePersonIds(ownerId, companyId);

      // Get properties directly linked to any owner-row fragment
      const directProps = await propertiesRepository.listIdsByOwnerIds(
        companyId,
        ownerIds,
      );

      // Also collect multi-owner property IDs from units.owner_id
      const unitOwned = await unitsRepository.listPropertyIdsByOwnerIds(
        companyId,
        ownerIds,
      );

      const propertyIds = [
        ...new Set([
          ...(directProps?.map((p) => p.id) ?? []),
          ...(unitOwned
            ?.map((u) => u.property_id)
            .filter((v): v is string => Boolean(v)) ?? []),
        ]),
      ];

      if (propertyIds.length === 0) {
        return {
          totalRevenue: 0,
          totalExpenses: 0,
          managementFees: 0,
          netDue: 0,
          statements: [],
        };
      }

      // Get receipts (revenue)
      const receipts = await receiptsRepository.listStatementByProperties(
        companyId,
        propertyIds,
      );

      // Get expenses — flipped to Nest (staff finance oracle ∩ this owner's
      // properties), Supabase fallback inside the repository using the
      // properties.owner_id-derived propertyIds above.
      const expenses = await expensesRepository.listForOwnerView(
        companyId,
        ownerId,
        propertyIds,
      );

      const totalRevenue =
        receipts?.reduce((sum, r) => sum + Number(r.amount), 0) || 0;
      const ownerExpenses =
        expenses.filter((e) => e.payment_responsibility === "owner") || [];
      const totalExpenses = ownerExpenses.reduce(
        (sum, e) => sum + Number(e.total_amount),
        0,
      );
      const managementFees = totalRevenue * 0.1; // 10% fee
      const netDue = totalRevenue - totalExpenses - managementFees;

      // Build statement entries
      const statements: {
        id: string;
        date: string | null;
        type: "income" | "expense";
        description: string;
        amount: number;
        reference: string | null;
      }[] = [];

      receipts?.forEach((r) => {
        statements.push({
          id: r.id,
          date: r.payment_date,
          type: "income",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          description: `إيجار - ${(r.property as any)?.name || ""} ${(r.unit as any)?.unit_number ? `- وحدة ${(r.unit as any).unit_number}` : ""}`,
          amount: Number(r.amount),
          reference: r.receipt_number,
        });
      });

      ownerExpenses.forEach((e) => {
        statements.push({
          id: e.id,
          date: e.expense_date,
          type: "expense",
          description: e.description,
          amount: -Number(e.total_amount),
          reference: null,
        });
      });

      // Sort by date
      statements.sort(
        (a, b) => new Date(b.date ?? 0).getTime() - new Date(a.date ?? 0).getTime(),
      );

      return {
        totalRevenue,
        totalExpenses,
        managementFees,
        netDue,
        statements,
      };
    },
    enabled: !!ownerId && !!companyId,
  });
}

// Hook to get owner's contracts (brokerage agreements) — resolves full person
export function useOwnerContracts(ownerId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["owner-contracts-list", ownerId, companyId],
    queryFn: async () => {
      if (!ownerId || !companyId) return [];

      const ownerIds = await ownersRepository.resolvePersonIds(ownerId, companyId);

      return contractsRepository.listByOwnerIds(companyId, ownerIds);
    },
    enabled: !!ownerId && !!companyId,
  });
}

// Hook to update an owner
export function useUpdateOwner() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { language } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async ({
      id,
      ...data
    }: { id: string } & Partial<CreateOwnerData>) => {
      if (!companyId) throw new Error("Company not found");

      if (data.phone) {
        await assertPhoneAvailableAcrossCompanies(data.phone, companyId, language as "ar" | "en");
      }
      if (data.national_id) {
        await assertNationalIdAvailableAcrossCompanies(data.national_id, companyId, language as "ar" | "en");
      }
      if (data.email) {
        await assertEmailAvailableAcrossCompanies(data.email, companyId, language as "ar" | "en");
      }

      const owner = await ownersRepository.update(id, data, companyId);
      if (!owner) throw new Error("Owner not found or access denied");
      return owner;
    },
    onSuccess: (_updated, variables) => {
      queryClient.invalidateQueries({ queryKey: ["owners-with-stats"] });
      queryClient.invalidateQueries({ queryKey: ["owner-details"] });
      // Refresh the single-owner record EditOwnerDialog prefills from
      // (useOwnerFullRecord), scoped to the updated owner — else a stale
      // vat_number could be re-saved on re-open. Distinct key from the portal's
      // ["owner-record"], so this no longer over-invalidates that hook.
      queryClient.invalidateQueries({ queryKey: ["owner-full-record", variables.id] });
      // Owner type / establishment / representative may have changed → refresh
      // the picker metadata too.
      queryClient.invalidateQueries({ queryKey: ["owner-entity-meta"] });
    },
  });
}

// Hook to deactivate an owner (soft-delete)
export function useDeactivateOwner() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();

  return useDemoSafeMutation({
    mutationFn: async (ownerId: string) => {
      if (!companyId) throw new Error("Company not found");

      await ownersRepository.deactivate(ownerId, companyId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owners-with-stats"] });
      queryClient.invalidateQueries({ queryKey: ["users-stats"] });
      queryClient.invalidateQueries({ queryKey: ["owner-entity-meta"] });
    },
  });
}

// Hook to link a property to an owner
export function useLinkPropertyToOwner() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();

  return useDemoSafeMutation({
    mutationFn: async ({
      propertyId,
      ownerId,
    }: {
      propertyId: string;
      ownerId: string;
    }) => {
      if (!companyId) throw new Error("No company ID");

      await propertiesRepository.setOwner(propertyId, ownerId, companyId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owners-with-stats"] });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success("تم ربط العقار بالمالك");
    },
    onError: (error: Error) => {
      toast.error(error.message || "حدث خطأ أثناء الربط");
    },
  });
}
